import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container, Spinner } from "react-bootstrap";
import "../css/menu.css";
import "../css/meal.css";
import "../css/main.css";
import "../css/cart.css";
import Footer from "../components/footer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import { v4 as uuid } from "uuid";

// Custom hook for cart management with localStorage
const useCartWithLocalStorage = (prefix) => {
  const [cart, setCart] = useState(
    JSON.parse(localStorage.getItem(prefix + "-cart")) || []
  );

  useEffect(() => {
    localStorage.setItem(prefix + "-cart", JSON.stringify(cart));
  }, [cart, prefix]);
  
  return [cart, setCart];
};

// Cart Item Component
const CartItem = ({ item, updateCart, removeFromCart, info, lang }) => {
  return (
    <div className="cart-item" style={{ marginBottom: "15px" }}>
      <div className="row align-items-center">
        <div className="col-8">
          <h5>{item.meal.name}</h5>
          <p className="options">
            {item.checkedOptions.map((opt) => opt.name).join(", ")}
          </p>
        </div>
        <div className="col-4 text-end">
          <div className="price">{(item.total + item.optPrice).toFixed(2)} ₺</div>
        </div>
      </div>
      
      <div className="row align-items-center mt-2">
        <div className="col-6">
          <div className="quantity-control d-flex align-items-center">
            <button 
              className="btn btn-sm" 
              onClick={() => {
                if (item.quantity > 1) {
                  item.quantity--;
                  updateCart(item);
                } else {
                  removeFromCart(item);
                }
              }}
              style={{ backgroundColor: info?.color, color: info?.textColor }}
            >
              <i className="bi bi-dash"></i>
            </button>
            
            <span className="mx-2">{item.quantity}</span>
            
            <button 
              className="btn btn-sm" 
              onClick={() => {
                item.quantity++;
                updateCart(item);
              }}
              style={{ backgroundColor: info?.color, color: info?.textColor }}
            >
              <i className="bi bi-plus"></i>
            </button>
          </div>
        </div>
        
        <div className="col-6 text-end">
          <button 
            className="btn btn-sm" 
            onClick={() => removeFromCart(item)}
            style={{ color: "red" }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </div>
      
      <hr style={{ margin: "15px 0" }} />
    </div>
  );
};

// User Authentication Popup
const UserAuthPopup = ({ 
  showPopup, 
  setShowPopup, 
  phoneNumber, 
  setPhoneNumber, 
  info, 
  checkUserAndSubmit,
  showPopup2,
  userDiscount,
  discountAmountRef,
  submit,
  clicked
}) => {
  return (
    <div className={showPopup ? "overlay visible" : "overlay"}>
      <div className="popup">
        <div
          className="close"
          onClick={() => setShowPopup(false)}
        >
          &times;
        </div>
        <div className="content">
          <form>
            {showPopup2 == false ? (
              <>
                <p style={{ textAlign: "center", fontWeight: "bold" }}>
                  {info?.campaignText}
                </p>

                <div className="form-group">
                  <label htmlFor="phoneInput">Telefon Numarası</label>
                  <input
                    id="phoneInput"
                    type="number"
                    className="form-control"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>

                <div className="row">
                  <div
                    className="btn w-50 mx-auto mt-5"
                    style={{
                      backgroundColor: info?.color,
                    }}
                    onClick={checkUserAndSubmit}
                  >
                    Gönder
                  </div>
                </div>
              </>
            ) : (
              <>
                <p style={{ textAlign: "center", fontWeight: "bold" }}>
                  {info?.campaignOrderCount}: {userDiscount?.toFixed(2)}
                </p>

                <div className="form-group">
                  <label htmlFor="discountInput">Kullanılacak Miktar</label>
                  <input
                    id="discountInput"
                    type="text"
                    className="form-control"
                    placeholder="Puanı Giriniz."
                    ref={discountAmountRef}
                  />
                </div>

                <div className="row">
                  <div
                    className="btn w-50 mx-auto mt-5"
                    style={{
                      backgroundColor: info?.color,
                    }}
                    onClick={submit}
                  >
                    Gönder
                  </div>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  // Router and location setup
  const history = useHistory();
  const { cafe, table, menu, info, noFooter } = useLocation().state;
  
  // State management
  const [cart, setCart] = useCartWithLocalStorage(cafe);
  const note = useRef();
  const [loaded, setLoaded] = useState(false);
  const discountAmountRef = useRef();
  const [clicked, Click] = useState(false);
  const lang = localStorage.getItem("alka-menu-lang") || "tr";
  
  // User system states
  const [showPopup, setShowPopup] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userDiscount, setUserDiscount] = useState(0);

  // Utility functions
  const calculateTotal = () => {
    let total = 0;
    cart.forEach((c) => (total += (c.total + c.optPrice) * c.quantity));
    return total;
  };

  const goBack = () => {
    history.goBack();
  };

  const updateCart = (item = null) => {
    // If a specific item was passed, update its quantity
    if (item) {
      // Ensure quantity is at least 1
      if (item.quantity < 1) {
        item.quantity = 1;
      }
      
      // Find the item in the cart and update it
      const updatedCart = cart.map(cartItem => 
        cartItem === item ? { ...cartItem, quantity: item.quantity } : cartItem
      );
      
      setCart(updatedCart);
    } else {
      // Just update the cart to refresh the UI
      setCart([...cart.filter(c => c)]);
    }
    
    // Update localStorage
    localStorage.setItem(cafe + "-cart", JSON.stringify(cart));
  };

  const removeFromCart = (item) => {
    const newCart = cart.filter((c) => c !== item);
    setCart(newCart);
    
    // Update localStorage
    localStorage.setItem(cafe + "-cart", JSON.stringify(newCart));
  };

  // Cart actions
  const handleButtonClick = () => {
    const confirmMessage =
      lang === "tr"
        ? "Sepeti temizlemek istediğinize emin misiniz?"
        : lang === "en"
          ? "Are you sure you want to clear the cart?"
          : "هل ترغب حقًا في مسح العربة؟";

    confirmAlert({
      title:
        lang === "tr"
          ? "Sepeti Temizle"
          : lang === "en"
            ? "Clear Cart"
            : "مسح العربة",
      message: confirmMessage,
      buttons: [
        {
          label: lang === "tr" ? "Evet" : lang === "en" ? "Yes" : "نعم",
          onClick: () => clearCart(1),
        },
        {
          label: lang === "tr" ? "Hayır" : lang === "en" ? "No" : "لا",
          onClick: () => console.log("Kullanıcı onay vermedi."),
        },
      ],
    });
  };

  const clearCart = (val) => {
    if (calculateTotal() === 0) {
      toast.error(
        lang === "tr"
          ? "Sepette Ürün yok."
          : lang === "en"
            ? "Cart is empty."
            : "العربة فارغة",
        {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      return;
    }
    
    setCart([]);
    
    if (val === 0) return;
    
    const successMessage =
      lang === "tr"
        ? "Sepet Temizlendi!"
        : lang === "en"
          ? "Cart Cleared!"
          : "تم مسح العربة!";
          
    toast.success(successMessage, {
      position: "bottom-center",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    
    history.goBack();
  };

  // Order processing functions
  const giveOrder = (e, i = 0) => {
    if (calculateTotal() == 0) {
      toast.error(
        lang === "tr" 
          ? "Sepette Ürün yok."
          : lang === "en"
            ? "Cart is empty."
            : "العربة فارغة",
        {
          position: "bottom-center",
          autoClose: 1500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      return;
    }

    if (info?.hasUserSystem && i == 0) {
      setShowPopup(true);
      return;
    }

    var today = new Date();
    let cartItems = cart.map((c) => ({
      ...c,
      meal: c.meal.id,
      checkedOptions: c.checkedOptions.map((opt) => ({
        name: opt.name,
        id: opt.id,
      })),
    }));
    
    const order = {
      cart: cartItems,
      note: note.current.value,
      cafe,
      table,
      date: today.toLocaleDateString(),
    };
    
    const orderText =
      table +
      "\n" +
      cart.map((x) => x.quantity + " x " + x.meal.name + " \n") +
      "Toplam: " +
      calculateTotal() +
      "₺";
      
    if (info?.wpNumber && info?.wpNumber.length > 0) {
      window.location.href = `https://wa.me/${info.wpNumber}?text=${encodeURIComponent(orderText)}`;
    }
    
    const options = {
      method: "POST",
      body: JSON.stringify(order),
      headers: { "Content-Type": "applications/json" },
    };
    
    setLoaded(true);
    
    fetch(
      "https://us-central1-qr-p-94567.cloudfunctions.net/sendOrder",
      options
    ).then(() => {
      clearCart(0);
      toast.success(
        lang === "tr"
          ? "Sipariş Verildi!"
          : lang === "en"
            ? "Order Placed!"
            : "تم تقديم الطلب!",
        {
          position: "bottom-center",
          autoClose: 1500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      history.push("/m/" + cafe + "/" + table);
    });
  };

  const submit = async () => {
    var today = new Date();
    const requestId = uuid();
    let cartItems = cart.map((c) => ({ ...c, meal: c.meal.id }));
    let order;
    
    if (info.hasUserSystem) {
      let phone = phoneNumber.replace(/\D/g, "").replace(/\s+/g, "");
      if (phone.charAt(0) != "0") {
        phone = "0" + phone;
      }
      
      if (showPopup2 == true) {
        if (!discountAmountRef.current.value) {
          toast.error("Kullanmak istediğiniz puan miktarını giriniz!", {
            position: "bottom-center",
            autoClose: 2500,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
          return;
        }
        
        if (parseFloat(discountAmountRef.current.value) > calculateTotal()) {
          toast.error(
            "Kullanmak istediğiniz puan miktarı toplam tutardan fazla olamaz!",
            {
              position: "bottom-center",
              autoClose: 2500,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }
          );
          return;
        }
        
        if (parseFloat(discountAmountRef.current.value) > userDiscount) {
          toast.error(
            "Kullanmak istediğiniz puan miktarı toplam puanınızda fazla olamaz!",
            {
              position: "bottom-center",
              autoClose: 2500,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }
          );
          return;
        }
        
        order = {
          requestId,
          cart: cartItems,
          note: note.current.value,
          cafe,
          table,
          phone,
          discountAmount: discountAmountRef.current.value,
        };
      } else {
        order = {
          requestId,
          cart: cartItems,
          note: note.current.value,
          cafe,
          table,
          phone,
        };
      }
    } else {
      order = {
        requestId,
        cart: cartItems,
        note: note.current.value,
        cafe,
        table,
      };
    }
    
    setLoaded(true);
    setShowPopup(false);
    setShowPopup2(false);
    
    const options = {
      method: "POST",
      body: JSON.stringify(order),
      headers: { "Content-Type": "applications/json" },
    };
    
    fetch(
      "https://us-central1-qr-p-94567.cloudfunctions.net/sendOrder",
      options
    ).then(() => {
      clearCart(0);
      toast.success(
        lang === "tr"
          ? "Sipariş Verildi!"
          : lang === "en"
            ? "Order Placed!"
            : "تم تقديم الطلب!",
        {
          position: "bottom-center",
          autoClose: 1500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      Click(false);
      history.push("/m/" + cafe + "/" + table);
    });
  };

  const checkUserAndSubmit = async () => {
    if (!phoneNumber) {
      toast.error("Eksik Giriş", {
        position: "bottom-center",
        autoClose: 3500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    
    setLoaded(true);
    setShowPopup(false);
    
    let phone = phoneNumber.replace(/\D/g, "").replace(/\s+/g, "");
    if (phone.charAt(0) != "0") {
      phone = "0" + phone;
    }
    
    const options = {
      method: "POST",
      body: JSON.stringify({ phone, cafe }),
      headers: { "Content-Type": "applications/json" },
    };
    
    const { discount } = await (
      await fetch(
        "https://us-central1-qr-p-94567.cloudfunctions.net/getAmount",
        options
      )
    ).json();
    
    setUserDiscount(discount);
    let nextDiscount = parseFloat(discount) + calculateTotal() * 0.1;
    
    if (discount >= 20) {
      setLoaded(false);
      setShowPopup(true);
      setShowPopup2(true);
      discountAmountRef.current.value = "";
      return;
    } else if (nextDiscount > 20) {
      toast.info(info.aa, {
        position: "bottom-center",
        autoClose: 4500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else {
      toast.info(nextDiscount.toFixed(2) + " TL " + info.bb, {
        position: "bottom-center",
        autoClose: 4500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    
    submit();
  };

  // Translations
  const getTranslation = (key) => {
    const translations = {
      cart: {
        tr: "Sepet",
        en: "Cart",
        ar: "العربة"
      },
      total: {
        tr: "Toplam Tutar: ",
        en: "Total Price: ",
        ar: "السعر الكلي"
      },
      addNote: {
        tr: "Not Ekle",
        en: "Add a Note",
        ar: "اضف ملاحظة"
      },
      confirm: {
        tr: "Onayla",
        en: "Give Order",
        ar: "اعطاء أوامر"
      },
      clear: {
        tr: "Temizle",
        en: "Clear cart",
        ar: "حذف الكل"
      }
    };
    
    return translations[key][lang] || translations[key].tr;
  };

  return (
    <>
      {/* Loading spinner */}
      {loaded && (
        <div className="spinner-container">
          <Spinner animation="border" role="status" className="spinner">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Main cart container */}
      <div
        className="cart container mate"
        style={{
          backgroundColor: info?.backgroundColor,
          color: info?.textColor,
          minHeight: "100vh",
        }}
      >
        {/* Back button */}
        <div
          className="back-cart mt-3"
          style={{ color: info?.backColor }}
          onClick={goBack}
        >
          <i className="bi bi-chevron-double-left"></i>
        </div>

        {/* Cart contents */}
        <div id="restaurant-menu">
          <Container>
            <div className="section-title text-center">
              <h2 className="section-title" style={{ paddingTop: 30 }}>
                {getTranslation("cart")}
              </h2>
            </div>

            {/* Cart items */}
            {cart?.length > 0 ? (
              cart.map((item, index) => (
                <CartItem 
                  key={index}
                  item={item}
                  updateCart={updateCart}
                  removeFromCart={removeFromCart}
                  info={info}
                  lang={lang}
                />
              ))
            ) : (
              <div className="text-center mt-4 mb-4">
                <p>{lang === "tr" ? "Sepetiniz boş" : lang === "en" ? "Your cart is empty" : "عربة التسوق فارغة"}</p>
              </div>
            )}
            
            {/* Cart summary */}
            {cart?.length > 0 && (
              <div className="cart-summary">
                <div className="cart-total d-flex justify-content-between">
                  <span>{getTranslation("total")}</span>
                  <span>{calculateTotal().toFixed(2)} ₺</span>
                </div>
              </div>
            )}
          </Container>
          
          {/* Note section and actions */}
          <Container>
            <div className="note-section mt-4">
              <h5>{getTranslation("addNote")}</h5>
              <div className="input-group" id="meal-note">
                <textarea
                  className="form-control"
                  aria-label="With textarea"
                  ref={note}
                  placeholder={lang === "tr" ? "Siparişiniz için not..." : lang === "en" ? "Note for your order..." : "ملاحظة لطلبك..."}
                  style={{
                    borderRadius: 10,
                    height: "12vh",
                  }}
                ></textarea>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="cart-actions">
              <button
                className="btn menuButton"
                onClick={(e) => giveOrder(e)}
                style={{
                  backgroundColor: info?.color,
                  color: info?.textColor,
                }}
                disabled={cart?.length === 0}
              >
                {getTranslation("confirm")}
              </button>
              
              <button
                className="btn menuButton2"
                onClick={handleButtonClick}
                style={{
                  color: info?.textColor,
                }}
                disabled={cart?.length === 0}
              >
                {getTranslation("clear")}
              </button>
            </div>
          </Container>
        </div>
        <div style={{ height: "15vh" }}></div>
      </div>

      {/* User auth popup */}
      <UserAuthPopup 
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        info={info}
        checkUserAndSubmit={checkUserAndSubmit}
        showPopup2={showPopup2}
        userDiscount={userDiscount}
        discountAmountRef={discountAmountRef}
        submit={submit}
        clicked={clicked}
      />

      {/* Footer */}
      {!noFooter && <Footer cafe={cafe} table={table} menu={menu} info={info} active={2} />}
    </>
  );
};

export default Cart;
